import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { formatValue, type BaseChartConfig, type Widget } from "./types";

interface TableConfig extends BaseChartConfig {
  columns?: string[];
  /** Default rows-per-page; user can still pick from the size menu. */
  page_size?: number;
}

export function TableCard({ widget }: { widget: Widget }) {
  const cfg = widget.config as TableConfig;
  const allRows = (widget.data?.points ?? []) as Record<string, unknown>[];
  const cols = cfg.columns?.length
    ? cfg.columns
    : allRows.length
      ? Object.keys(allRows[0])
      : [];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(cfg.page_size ?? 10);
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [orderDir, setOrderDir] = useState<"asc" | "desc">("asc");

  // Sort
  const sorted = useMemo(() => {
    if (!orderBy) return allRows;
    const copy = [...allRows];
    copy.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return orderDir === "asc" ? av - bv : bv - av;
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      return orderDir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return copy;
  }, [allRows, orderBy, orderDir]);

  // Page slice
  const start = page * rowsPerPage;
  const slice = sorted.slice(start, start + rowsPerPage);

  function toggleSort(col: string) {
    if (orderBy === col) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(col);
      setOrderDir("desc");
    }
  }

  return (
    <Card variant="outlined">
      {widget.title && (
        <CardHeader
          title={widget.title}
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 700, letterSpacing: "-0.01em" }}
          subheaderTypographyProps={{ variant: "caption", color: "text.secondary" }}
          sx={{ pb: 1 }}
        />
      )}
      <CardContent sx={{ pt: 0, pb: 0 }}>
        {allRows.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {widget.data?.error ? `No data (${widget.data.error})` : "No data."}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: { xs: 340, md: 460 }, overflowX: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {cols.map((c) => (
                      <TableCell
                        key={c}
                        sortDirection={orderBy === c ? orderDir : false}
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          textTransform: "uppercase",
                          fontSize: 11,
                          letterSpacing: "0.05em",
                          backgroundColor: "background.paper",
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === c}
                          direction={orderBy === c ? orderDir : "asc"}
                          onClick={() => toggleSort(c)}
                        >
                          {c}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slice.map((r, i) => (
                    <TableRow key={start + i} hover>
                      {cols.map((c) => {
                        const v = r[c];
                        const display =
                          typeof v === "number"
                            ? formatValue(v, cfg.format)
                            : v == null
                              ? "—"
                              : String(v);
                        return <TableCell key={c}>{display}</TableCell>;
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={allRows.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                borderTop: 1,
                borderColor: "divider",
                ".MuiTablePagination-toolbar": { minHeight: 44 },
              }}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
